import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import PageHeader from '../components/PageHeader';
import { memberController } from '../controllers/memberController';
import ModalShell from '../components/ModalShell';
import { positionsAPI } from '@/services';
import { useAuth } from '../contexts/AuthContext';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import { searchFieldsMatch } from '../utils/searchText';

const Commissions = () => {
  const { success, error } = useToast();
  const { confirm } = useConfirmDialog();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { roles: assignableRoles } = useAssignableRoles();

  // ── Commission state ──
  const [commissions, setCommissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingCommissions, setLoadingCommissions] = useState(true);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ name: '', description: '' });
  const [expandedCommissions, setExpandedCommissions] = useState(new Set());

  // ── Position state ──
  const [positions, setPositions] = useState([]);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [savingPosition, setSavingPosition] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [editingPositionId, setEditingPositionId] = useState('');
  const [positionForm, setPositionForm] = useState({
    key: '',
    label: '',
    description: '',
    role_keys: ['participant'],
    commission_id: '',
  });

  // ── Data loading ──
  useEffect(() => {
    loadCommissions();
    loadPositions();
  }, []);

  const loadCommissions = async () => {
    try {
      setLoadingCommissions(true);
      const { rows } = await memberController.fetchCommissions();
      setCommissions(rows);
    } catch (err) {
      console.error('Failed to load commissions:', err);
      error('Erreur lors du chargement des commissions');
    } finally {
      setLoadingCommissions(false);
    }
  };

  const loadPositions = useCallback(async () => {
    try {
      setLoadingPositions(true);
      const res = await positionsAPI.getList();
      setPositions(res.data?.positions || []);
    } catch (_err) {
      setPositions([]);
    } finally {
      setLoadingPositions(false);
    }
  }, []);

  // ── Derived data ──
  const filteredCommissions = useMemo(() => {
    const source = Array.isArray(commissions) ? commissions : [];
    if (!searchQuery.trim()) return source;
    return source.filter((c) => searchFieldsMatch(searchQuery, [c.name, c.description]));
  }, [commissions, searchQuery]);

  const positionsByCommission = useMemo(() => {
    const map = {};
    for (const p of positions) {
      const cid = String(p.commission_id || '').trim();
      if (!cid) continue;
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    }
    return map;
  }, [positions]);

  const unlinkedPositions = useMemo(
    () => positions.filter((p) => !String(p.commission_id || '').trim()),
    [positions]
  );

  const commissionLabelById = useMemo(() => {
    const map = {};
    for (const c of commissions) map[c.id] = c.name;
    return map;
  }, [commissions]);

  // ── Commissions handlers ──
  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCommission) {
        await memberController.updateCommission(selectedCommission.id, commissionForm);
        success('Commission mise à jour avec succès');
      } else {
        await memberController.createCommission(commissionForm);
        success('Commission créée avec succès');
      }
      loadCommissions();
      setShowCommissionForm(false);
      resetCommissionForm();
    } catch (err) {
      console.error('Failed to save commission:', err);
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde de la commission');
    }
  };

  const handleDeleteCommission = async (id) => {
    const commission = commissions.find((c) => c.id === id);
    const linkedCount = (positionsByCommission[id] || []).length;
    const confirmed = await confirm({
      title: 'Supprimer la commission',
      message: `Êtes-vous sûr de vouloir supprimer la commission "${commission?.name}" ?${linkedCount > 0 ? ` ${linkedCount} position(s) associée(s) sera/seront détachée(s).` : ''} Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      await memberController.deleteCommission(id);
      success('Commission supprimée avec succès');
      loadCommissions();
    } catch (err) {
      console.error('Failed to delete commission:', err);
      error(err.response?.data?.detail || 'Erreur lors de la suppression de la commission');
    }
  };

  const handleEditCommission = (commission) => {
    setSelectedCommission(commission);
    setCommissionForm({ name: commission.name || '', description: commission.description || '' });
    setShowCommissionForm(true);
  };

  const resetCommissionForm = () => {
    setCommissionForm({ name: '', description: '' });
    setSelectedCommission(null);
  };

  const toggleExpand = (id) => {
    setExpandedCommissions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Positions handlers ──
  const businessRoleOptions = useMemo(() => {
    const deny = new Set([
      'saas_admin', 'org_admin', 'org_manager', 'admin_project',
      'admin', 'manager', 'member', 'membre',
    ]);
    const fromApi = (assignableRoles || [])
      .map((r) => ({ key: String(r?.key || '').trim(), label: String(r?.label || r?.key || '').trim() }))
      .filter((r) => r.key && !deny.has(r.key.toLowerCase()));
    if (fromApi.length > 0) return fromApi;
    return [
      { key: 'executive', label: 'Rôle exécutif' },
      { key: 'finance_lead', label: 'Responsable finances' },
      { key: 'operations_lead', label: 'Responsable opérations' },
      { key: 'participant', label: 'Participant' },
    ];
  }, [assignableRoles]);

  const toggleRoleKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return;
    setPositionForm((prev) => {
      const has = (prev.role_keys || []).includes(k);
      const next = has ? (prev.role_keys || []).filter((x) => x !== k) : [...(prev.role_keys || []), k];
      return { ...prev, role_keys: next };
    });
  };

  const openCreatePosition = (preselectedCommissionId = '') => {
    setEditingPositionId('');
    setPositionForm({
      key: '', label: '', description: '',
      role_keys: ['participant'],
      commission_id: preselectedCommissionId,
    });
    setShowPositionModal(true);
  };

  const openEditPosition = (row) => {
    setEditingPositionId(String(row?.id || ''));
    setPositionForm({
      key: String(row?.key || ''),
      label: String(row?.label || ''),
      description: String(row?.description || ''),
      role_keys: Array.isArray(row?.role_keys) ? row.role_keys : [],
      commission_id: String(row?.commission_id || ''),
    });
    setShowPositionModal(true);
  };

  const handleSavePosition = async (e) => {
    e.preventDefault();
    const payload = {
      label: String(positionForm.label || '').trim(),
      description: String(positionForm.description || '').trim(),
      role_keys: Array.isArray(positionForm.role_keys) ? positionForm.role_keys : [],
      commission_id: String(positionForm.commission_id || '').trim(),
    };
    if (!editingPositionId) {
      payload.key = String(positionForm.key || '').trim();
    }
    if (!payload.label) { error('Le libellé est requis.'); return; }
    if (!editingPositionId && !payload.key) { error('La clé est requise.'); return; }
    try {
      setSavingPosition(true);
      if (editingPositionId) {
        await positionsAPI.update(editingPositionId, payload);
        success('Position mise à jour.');
      } else {
        await positionsAPI.create(payload);
        success('Position créée.');
      }
      setShowPositionModal(false);
      await loadPositions();
    } catch (err) {
      error(err?.response?.data?.detail || "Échec de l'enregistrement de la position.");
    } finally {
      setSavingPosition(false);
    }
  };

  const handleDeletePosition = async (row) => {
    const rid = String(row?.id || '').trim();
    if (!rid) return;
    const confirmed = await confirm({
      title: 'Supprimer la position',
      message: `Êtes-vous sûr de vouloir supprimer la position "${row?.label || row?.key || rid}" ? Les membres avec cette position perdront les permissions associées.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
    });
    if (!confirmed) return;
    try {
      await positionsAPI.delete(rid);
      success('Position supprimée.');
      await loadPositions();
    } catch (err) {
      error(err?.response?.data?.detail || 'Impossible de supprimer la position.');
    }
  };

  const canEdit = hasPermission('commissions.edit') || hasPermission('members.edit') || hasPermission('users.edit');

  // ── Shared position row component ──
  const PositionRow = ({ p, compact }) => (
    <div className={`flex items-center justify-between gap-2 ${compact ? 'py-2' : 'py-3'}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
          <span className="font-medium text-sm text-gray-900 truncate">{p.label || p.key}</span>
        </div>
        {p.description && <p className="text-xs text-gray-500 mt-0.5 ml-6 line-clamp-1">{p.description}</p>}
        <div className="flex flex-wrap gap-1 mt-1 ml-6">
          {(p.role_keys || []).map((rk) => (
            <span key={rk} className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0 text-[10px] font-medium text-amber-800">
              {rk}
            </span>
          ))}
        </div>
      </div>
      {canEdit && (
        <div className="shrink-0 flex items-center gap-1">
          <button onClick={() => openEditPosition(p)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Modifier">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
          </button>
          <button onClick={() => handleDeletePosition(p)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Supprimer">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
          </button>
        </div>
      )}
    </div>
  );

  if (loadingCommissions && commissions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      <PageHeader
        title="Commissions & Positions"
        subtitle="Organisation des comités et des rôles du projet"
        actions={
          canEdit && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => openCreatePosition()}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm whitespace-nowrap"
              >
                + Position
              </button>
              <button
                onClick={() => { resetCommissionForm(); setShowCommissionForm(true); }}
                className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap"
              >
                + Commission
              </button>
            </div>
          )
        }
      />

      <SearchAndFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Rechercher une commission..."
        filters={[]}
      />

      {/* ── Commission cards with embedded positions ── */}
      <div className="space-y-4">
        {filteredCommissions.map((commission) => {
          const linkedPositions = positionsByCommission[commission.id] || [];
          const isExpanded = expandedCommissions.has(commission.id);

          return (
            <div
              key={commission.id}
              className="bg-white rounded-2xl md:rounded-xl shadow-xl md:shadow-sm border-0 md:border border-gray-200 overflow-hidden transition-all"
            >
              {/* Commission header */}
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="shrink-0 p-3 bg-gradient-to-br from-entrea-ink to-entrea-royal md:bg-entrea-panel rounded-2xl md:rounded-xl shadow-lg shadow-entrea-royal/25 md:shadow-none">
                      <svg className="h-7 w-7 text-white md:text-entrea-royal" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{commission.name}</h3>
                      {commission.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{commission.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        {commission.members_count > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-entrea-panel px-2 py-0.5 font-medium text-entrea-royal">
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            {commission.members_count} membre{commission.members_count > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                          {linkedPositions.length} position{linkedPositions.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    {canEdit && (
                      <>
                        <button onClick={() => openCreatePosition(commission.id)} className="p-2 text-gray-400 hover:text-entrea-royal hover:bg-entrea-panel rounded-lg transition" title="Ajouter une position">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                        </button>
                        <button onClick={() => handleEditCommission(commission)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title="Modifier">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
                        </button>
                        <button onClick={() => handleDeleteCommission(commission.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Supprimer">
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        </button>
                      </>
                    )}
                    {linkedPositions.length > 0 && (
                      <button onClick={() => toggleExpand(commission.id)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition" title={isExpanded ? 'Réduire' : 'Développer'}>
                        <svg className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Linked positions (expandable) */}
              {linkedPositions.length > 0 && isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50/50 px-5 sm:px-6 py-2 divide-y divide-gray-100">
                  {linkedPositions.map((p) => (
                    <PositionRow key={p.id} p={p} compact />
                  ))}
                </div>
              )}

              {/* Inline summary when collapsed */}
              {linkedPositions.length > 0 && !isExpanded && (
                <button
                  onClick={() => toggleExpand(commission.id)}
                  className="w-full border-t border-gray-100 bg-gray-50/30 px-5 sm:px-6 py-2.5 text-left hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <svg className="h-3.5 w-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span>{linkedPositions.map((p) => p.label || p.key).join(', ')}</span>
                    <svg className="h-3.5 w-3.5 ml-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Positions non rattachées ── */}
      {unlinkedPositions.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Positions non rattachées à une commission
          </h3>
          <div className="bg-white rounded-2xl md:rounded-xl shadow-xl md:shadow-sm border-0 md:border border-gray-200 divide-y divide-gray-100 px-5 sm:px-6">
            {unlinkedPositions.map((p) => (
              <PositionRow key={p.id} p={p} />
            ))}
          </div>
        </div>
      )}

      {/* Empty states */}
      {filteredCommissions.length === 0 && commissions.length > 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="mt-4 text-gray-900 font-medium">Aucune commission trouvée</p>
          <p className="mt-2 text-sm text-gray-500">Essayez de modifier vos critères de recherche</p>
        </div>
      )}

      {commissions.length === 0 && !loadingCommissions && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="mt-4 text-gray-900 font-medium">Aucune commission</p>
          <p className="mt-2 text-sm text-gray-500">Créez vos comités et définissez les positions de chaque membre</p>
          {canEdit && (
            <button
              onClick={() => { resetCommissionForm(); setShowCommissionForm(true); }}
              className="mt-6 px-6 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium"
            >
              + Créer une commission
            </button>
          )}
        </div>
      )}

      {/* ── Modal: Commission ── */}
      {showCommissionForm && (
        <ModalShell maxWidthClass="max-w-md" onBackdropClick={() => { setShowCommissionForm(false); resetCommissionForm(); }}>
          <div className="p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              {selectedCommission ? 'Modifier la commission' : 'Nouvelle commission'}
            </h2>
            <form onSubmit={handleCommissionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la commission *</label>
                <input
                  type="text"
                  required
                  value={commissionForm.name}
                  onChange={(e) => setCommissionForm({ ...commissionForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  placeholder="Ex: Comité de Gestion"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
                <textarea
                  value={commissionForm.description}
                  onChange={(e) => setCommissionForm({ ...commissionForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  rows="4"
                  placeholder="Responsable de l'administration globale du cadre..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowCommissionForm(false); resetCommissionForm(); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium">
                  Annuler
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium">
                  {selectedCommission ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </ModalShell>
      )}

      {/* ── Modal: Position ── */}
      {showPositionModal && (
        <ModalShell maxWidthClass="max-w-lg" onBackdropClick={() => !savingPosition && setShowPositionModal(false)}>
          <div className="p-4 sm:p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingPositionId ? 'Modifier la position' : 'Nouvelle position'}
            </h2>
            <form onSubmit={handleSavePosition} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission</label>
                <select
                  value={positionForm.commission_id}
                  onChange={(e) => setPositionForm((prev) => ({ ...prev, commission_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky bg-white"
                >
                  <option value="">— Aucune (position indépendante)</option>
                  {commissions.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clé technique *</label>
                <input
                  type="text"
                  required
                  disabled={Boolean(editingPositionId)}
                  value={positionForm.key}
                  onChange={(e) => setPositionForm((prev) => ({ ...prev, key: e.target.value }))}
                  placeholder="president"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
                <input
                  type="text"
                  required
                  value={positionForm.label}
                  onChange={(e) => setPositionForm((prev) => ({ ...prev, label: e.target.value }))}
                  placeholder="Président(e)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={2}
                  value={positionForm.description}
                  onChange={(e) => setPositionForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Supervise les activités, représente officiellement le cadre..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôles métier (role_keys)</label>
                <div className="rounded-lg border border-gray-300 p-2 space-y-2">
                  {businessRoleOptions.map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={(positionForm.role_keys || []).includes(opt.key)}
                        onChange={() => toggleRoleKey(opt.key)}
                        className="h-4 w-4 text-entrea-royal border-gray-300 rounded focus:ring-entrea-royal/40"
                      />
                      <span>{opt.label}</span>
                      <span className="text-xs font-mono text-gray-500">{opt.key}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Ces clés pilotent les permissions métier Entrea.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" disabled={savingPosition} onClick={() => setShowPositionModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                  Annuler
                </button>
                <button type="submit" disabled={savingPosition} className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition disabled:opacity-60">
                  {savingPosition ? 'Enregistrement...' : editingPositionId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default Commissions;
